import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { StudioId } from '@/domain/types';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import {
  callsPerAgent,
  agentPerformance,
  queueHealthByStudio,
} from '@/domain/reportingEngine';

function agoLabel(iso: string, nowIso: string): string {
  const ms = new Date(nowIso).getTime() - new Date(iso).getTime();
  const days = Math.floor(ms / (24 * 60 * 60_000));
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / (60 * 60_000));
  if (hours >= 1) return `${hours}h`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m`;
}

interface Props {
  /** The selected date-range window for calls + per-agent conversion. */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

/**
 * Calls & agents tab (Prompt F, Stage 4). Outbound + inbound calls per agent
 * (bars), per-agent conversion (the live table), and live queue health per
 * studio. Calls accrue live as the demo dials, so the bars start empty by design.
 */
export default function CallsTab({ days, studioId }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const customers = useBrainStore((s) => s.customers);
  const deposits = useBrainStore((s) => s.deposits);
  const agents = useBrainStore((s) => s.agents);
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const pieces = useBrainStore((s) => s.pieces);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const sequenceState = useBrainStore((s) => s.sequenceState);

  const agentName = (id: string) => {
    const a = agents[id];
    return a ? `${a.firstName} ${a.lastName}`.trim() : id;
  };

  const callsChart = useMemo(() => {
    const rows = callsPerAgent(days, studioId);
    return rows.map((r) => ({ name: agentName(r.agentId), Outbound: r.outbound, Inbound: r.inbound }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, studioId, customers, agents]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const perAgent = useMemo(() => agentPerformance(days, studioId), [days, studioId, customers, deposits, agents]);

  const queue = useMemo(
    () => queueHealthByStudio(clockIso, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clockIso, studioId, customers, deposits, bookings, pieces, rebookFollowupTasks, sequenceState, studios],
  );

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-calls">
      {/* Calls per agent */}
      <section className="panel p-4" data-testid="calls-per-agent">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Calls per agent
          <InfoTip>
            Outbound and inbound voice calls per agent in the selected span, from the durable call log. Calls are
            never seeded — they accrue live as the demo dials, so this starts empty by design (no invented numbers).
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Outbound + inbound this period — accrues live as the demo dials
        </p>
        {callsChart.length > 0 ? (
          <ReportBarChart
            data={callsChart}
            xKey="name"
            series={[
              { key: 'Outbound', name: 'Outbound', color: REPORT_COLORS.gold },
              { key: 'Inbound', name: 'Inbound', color: REPORT_COLORS.azure },
            ]}
          />
        ) : (
          <p className="font-serif text-cream-soft text-sm" data-testid="calls-empty">
            No calls dialled in this window yet. This fills in live during the demo — no invented numbers.
          </p>
        )}
      </section>

      {/* Per-agent conversion table */}
      <section className="panel p-4" data-testid="calls-agent-table">
        <h2 className="heading-serif text-lg text-cream mb-3">
          Per-agent conversion
          <InfoTip>
            Per bookings-agent for the selected span: outbound + inbound calls, deposits credited to them, and
            conversion = deposits ÷ total calls. A dash means no calls have been dialled yet (not zero conversion).
          </InfoTip>
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Agent</th>
                <th className="text-right px-2 py-2">Outbound</th>
                <th className="text-right px-2 py-2">Inbound</th>
                <th className="text-right px-2 py-2">Deposits</th>
                <th className="text-right px-2 py-2">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {perAgent.map((r) => (
                <tr key={r.agentId} className="border-t border-gold-dim/15" data-testid={`calls-agent-${r.agentId}`}>
                  <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{r.outboundCalls}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{r.inboundCalls}</td>
                  <td className="px-2 py-2 text-right font-mono text-gold-bright">{r.deposits}</td>
                  <td className={clsx('px-2 py-2 text-right font-mono', r.conversion === null ? 'text-cream-faint' : 'text-cream')}>
                    {r.conversion === null ? '—' : `${(r.conversion * 100).toFixed(0)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-3">
          Conversion = deposits ÷ calls · dash means no calls dialled yet
        </p>
      </section>

      {/* Queue health per studio */}
      <section data-testid="calls-queue-health">
        <h2 className="heading-serif text-lg text-cream mb-3">
          Queue health
          <InfoTip>
            Per studio: how many actionable items are waiting in the live operator queue (the same queue the ops
            screen shows), and how long the oldest one has been waiting.
          </InfoTip>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {queue.map((q) => (
            <div
              key={q.studioId}
              className={clsx('panel-dark p-4', q.depth > 0 && 'border-l-2 border-gold-bright/50')}
              data-testid={`calls-queue-${q.studioId}`}
            >
              <p className="label-small-caps">{q.name}</p>
              <p className="font-cinzel text-3xl text-cream mt-2">
                {q.depth}
                <span className="text-cream-faint text-lg"> waiting</span>
              </p>
              <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                {q.oldestIso ? `Oldest waiting ${agoLabel(q.oldestIso, clockIso)}` : 'Queue clear'}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
